import "./MyLeaveRequests.scss";
import React from "react";
import {
  Table,
  Button,
  Tooltip,
  Spin,
  Empty,
  Tag,
  Row,
  Col,
  Typography,
  Grid,
  DatePicker,
} from "antd";
import {
  CloseCircleOutlined,
  DownloadOutlined,
  EditOutlined,
  LeftOutlined,
  RightOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { ViewDocumentsBtn } from "../components/Buttons/ViewDocumentsBtn";
import { RequestIdLink } from "../components/Buttons/RequestIdLink";
import type { ColumnsType } from "antd/es/table";
import { SortOrder } from "antd/es/table/interface";
import {
  fetchData,
  filters,
  filterStatusMap,
  formatEnumLabel,
  getRequestStatusType,
  getYears,
  hasRoleAccess,
  Notify,
  RequestStatus,
  fixedTableScrollY,
  rowsPerPage,
} from "../utils/constants";
import {
  LeaveRequest,
  LeaveRequestPending,
  User,
  UserLeaveBalance,
} from "../utils/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Dayjs } from "dayjs";
import RequestStatusModal from "../components/Modals/RequestStatusModal";
import { useUser } from "../context/UserContext";
import { useNotifications } from "../context/NotificationsProvider";
import { ApexOptions } from "apexcharts";
import ReactApexChart from "react-apexcharts";
import LeaveReqModal from "../components/Modals/LeaveReqModal";
import AnnualLeavePlanModal from "../components/Modals/AnnualLeavePlanModal";
import AttachDocumentModal from "../components/Modals/AttachDocumentModal";
import { theme } from "antd";
import MyCalendar from "./MyCalendar";
import { formatRequestedAt } from "../utils/helpers";

const sortDirections: SortOrder[] = ["ascend", "descend"];

const DonutChart = React.memo(
  ({ userData, title }: { userData: UserLeaveBalance; title: string }) => {
    const { token } = theme.useToken();

    const data = React.useMemo(() => {
      return (
        userData || {
          currentYearDays: 0,
          previousYearDays: 0,
          usedCurrentYearDays: 0,
          usedPreviousYearDays: 0,
          availableDays: 0,
          employeeName: "",
        }
      );
    }, [userData]);

    const chartOptions: ApexOptions = React.useMemo(
      () => ({
        chart: { type: "donut", background: "transparent" },
        labels: [
          "Current Year Days",
          "Previous Year Days",
          "Used Current Year Days",
          "Used Previous Year Days",
        ],
        dataLabels: {
          enabled: true,
          style: { fontSize: "10px", fontWeight: "bold" },
          dropShadow: { enabled: false },
          offsetX: 0,
        },
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                name: { show: true },
                value: { show: true },
                total: {
                  show: true,
                  label: "Remaining days",
                  formatter: () => data.availableDays.toString(),
                },
              },
            },
          },
        },
        legend: { position: "bottom" },
        theme: {
          mode: token.colorBgBase === "#000" ? "dark" : "light",
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: { position: "bottom" },
            },
          },
        ],
      }),
      [data, token.colorBgBase],
    );

    const chartSeries = React.useMemo(
      () => [
        data.currentYearDays - data.usedCurrentYearDays,
        data.previousYearDays - data.usedPreviousYearDays,
        data.usedCurrentYearDays,
        data.usedPreviousYearDays,
      ],
      [data],
    );

    if (!userData) return null;

    return (
      <div className="pie-chart-container flex flex-col items-center">
        <h3>{title}</h3>
        <ReactApexChart
          options={chartOptions}
          series={chartSeries}
          type="donut"
          width={380}
        />
      </div>
    );
  },
);
DonutChart.displayName = "DonutChart";

const years = getYears();

// Only types whose document can't exist at creation time (see
// LeaveRequestDocumentController) - the document slot to upload into is
// implied by the request type.
const DEFERRED_DOCUMENT_TYPE_BY_REQUEST_TYPE: Record<string, string> = {
  BLOOD_DONATION: "BLOOD_DONATION_PROOF",
  MARRIAGE: "MARRIAGE_CERTIFICATE",
};

const MyLeaveRequests = () => {
  const { authUser, roles } = useUser();
  const { refreshLeaveRequests } = useNotifications();

  const [myLeaveRequests, setMyLeaveRequests] = useState<LeaveRequest[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<
    LeaveRequest | LeaveRequestPending | null
  >(null);
  const [selfUser, setSelfUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  const [selectedRequestStatus, setSelectedRequestStatus] =
    useState<RequestStatus>(RequestStatus.APPROVE);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserLeaveBalance | null>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(rowsPerPage);
  const [isLeaveBalanceVisible, setIsLeaveBalanceVisible] = useState(true);
  const isEmployeeOnly = !hasRoleAccess(roles, ["HR", "MANAGER", "ADMIN"]);
  const currentYear = years[1].value;

  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isAnnualPlanOpen, setIsAnnualPlanOpen] = useState(false);
  const [isAttachDocumentOpen, setIsAttachDocumentOpen] = useState(false);
  const [attachDocumentRequest, setAttachDocumentRequest] =
    useState<LeaveRequest | null>(null);
  const [isResubmitOpen, setIsResubmitOpen] = useState(false);
  const [resubmitRequest, setResubmitRequest] = useState<LeaveRequest | null>(
    null,
  );
  const [requestDateRange, setRequestDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >(null);
  const screens = Grid.useBreakpoint() || {};
  const isMobile = !screens.md;
  const { RangePicker } = DatePicker;

  const fetchUsers = useCallback(async () => {
    const api = isEmployeeOnly
      ? `${process.env.REACT_APP_API_URL}/users/getAllUsers`
      : `${process.env.REACT_APP_API_URL}/users/displayAllUsers`;
    try {
      await fetchData(api, setUsers);
    } catch (error) {
      console.error("Failed to fetch users data:", error);
      Notify("error", "Could not load users data. Please try again.");
    }
  }, [isEmployeeOnly]);

  const fetchMyLeaveReq = useCallback(async () => {
    const employeeId = selfUser?.id;
    if (typeof employeeId !== "number") return;
    try {
      setIsLoadingData(true);
      await fetchData(
        `${process.env.REACT_APP_API_URL}/getRequestsByEmployeeId/${employeeId}`,
        setMyLeaveRequests,
      );
    } catch (error) {
      console.error("Failed to fetch leave requests data:", error);
      Notify("error", "Could not load leave requests data. Please try again.");
    } finally {
      setIsLoadingData(false);
    }
  }, [selfUser]);

  const fetchLeaveBalance = useCallback(async () => {
    const employeeId = selfUser?.id;
    if (typeof employeeId !== "number") return;
    try {
      await fetchData(
        `${process.env.REACT_APP_API_URL}/leave-balance/details/${employeeId}/${currentYear}`,
        setUserData,
      );
    } catch (error) {
      console.error("Failed to fetch leave request data:", error);
      Notify("error", "Could not load leave request data. Please try again.");
    }
  }, [currentYear, selfUser]);

  const refreshPageData = useCallback(async () => {
    await Promise.all([fetchMyLeaveReq(), fetchLeaveBalance()]);
  }, [fetchLeaveBalance, fetchMyLeaveReq]);

  useEffect(() => {
    if (!selfUser) return;
    fetchLeaveBalance();
  }, [fetchLeaveBalance, selfUser]);

  useEffect(() => {
    if (!authUser) return;
    fetchUsers();
  }, [authUser, fetchUsers]);

  useEffect(() => {
    if (!selfUser) return;
    fetchMyLeaveReq();
  }, [selfUser, fetchMyLeaveReq]);

  useEffect(() => {
    if (authUser && users.length && !selfUser) {
      const self = users.find((u) => u.email === authUser.email);
      if (self) {
        setSelfUser(self);
      }
    }
  }, [authUser, selfUser, users]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, requestDateRange]);

  const filteredMyLeaveRequests = useMemo(() => {
    if (!requestDateRange?.[0] || !requestDateRange?.[1]) {
      return myLeaveRequests;
    }

    const filterStart = requestDateRange[0].startOf("day").toDate();
    const filterEnd = requestDateRange[1].endOf("day").toDate();

    return myLeaveRequests.filter((request) => {
      if (!request.startDate || !request.endDate) return false;
      const requestStart = new Date(request.startDate);
      const requestEnd = new Date(request.endDate);
      return requestStart <= filterEnd && requestEnd >= filterStart;
    });
  }, [myLeaveRequests, requestDateRange]);

  const documentsToUpload = useMemo(
    () =>
      myLeaveRequests.filter(
        (lq) =>
          !!lq.documentDeadline || lq.documents?.[0]?.reviewStatus === "REJECTED",
      ),
    [myLeaveRequests],
  );

  const documentUploadColumns = useMemo<ColumnsType<LeaveRequest>>(
    () => [
      {
        title: "Request Type",
        dataIndex: "requestType",
        key: "requestType",
        render: (value: LeaveRequest["requestType"]) => formatEnumLabel(value),
      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        render: (value: LeaveRequest["startDate"]) =>
          value ? new Date(value).toLocaleDateString("en-GB") : "-",
      },
      {
        title: "Status",
        key: "documentStatus",
        render: (_: unknown, record: LeaveRequest) => {
          const latest = record.documents?.[0];
          if (latest?.reviewStatus === "REJECTED") {
            return (
              <Tag color="red">
                Rejected{latest.reviewComment ? `: ${latest.reviewComment}` : ""}
              </Tag>
            );
          }
          if (record.documentOverdue) {
            return <Tag color="red">Overdue</Tag>;
          }
          return <Tag color="orange">Due {record.documentDeadline}</Tag>;
        },
      },
      {
        title: "Action",
        key: "action",
        width: 110,
        render: (_: unknown, record: LeaveRequest) => (
          <Button
            type="primary"
            size="small"
            icon={<UploadOutlined />}
            onClick={() => {
              setAttachDocumentRequest(record);
              setIsAttachDocumentOpen(true);
            }}
          >
            Upload
          </Button>
        ),
      },
    ],
    [],
  );

  const statusFilterOptions = useMemo(
    () =>
      filters.map((item) => ({
        text: item.name,
        value: filterStatusMap[item.name] || item.name,
      })),
    [],
  );

  const tableColumns = useMemo<ColumnsType<LeaveRequest>>(
    () => [
      {
        title: "Actions",
        key: "actions",
        width: 100,
        fixed: !isMobile ? "left" : undefined,
        render: (_: unknown, lq: LeaveRequest) => (
          <div className="flex flex-row gap-2">
            {["AWAITING_MANAGER", "AWAITING_ADMINISTRATIV", "RETURNED_FOR_CORRECTION"].includes(
              lq.requestStatus,
            ) && (
              <Tooltip title="Cancel pending request">
                <Button
                  type="text"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => {
                    setSelectedRequest(lq);
                    setSelectedRequestStatus(RequestStatus.CANCEL);
                    setIsModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {lq.requestStatus === "APPROVED" && (
              <Tooltip title="Cancel request">
                <Button
                  type="text"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => {
                    setSelectedRequest(lq);
                    setSelectedRequestStatus(RequestStatus.CANCEL);
                    setIsModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {lq.requestStatus === "RETURNED_FOR_CORRECTION" && (
              <Tooltip title="Edit & resubmit">
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setResubmitRequest(lq);
                    setIsResubmitOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {!!lq.leavePDF &&
              lq.requestStatus === "APPROVED" && (
              <Tooltip title="Download request">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    if (!lq.leavePDF) return;

                    const link = document.createElement("a");

                    const pdfDataUrl = lq.leavePDF.startsWith("data:")
                      ? lq.leavePDF
                      : `data:application/pdf;base64,${lq.leavePDF}`;

                    link.href = pdfDataUrl;
                    link.download = `Cerere-concediu-ReqNo-${lq.id}-${selfUser?.firstName || ""}-${selfUser?.lastName || ""}.pdf`;

                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                />
              </Tooltip>
            )}
            {(!!lq.documentDeadline || lq.documents?.[0]?.reviewStatus === "REJECTED") && (
              <Tooltip
                title={
                  lq.documents?.[0]?.reviewStatus === "REJECTED"
                    ? `Document rejected${lq.documents[0].reviewComment ? `: ${lq.documents[0].reviewComment}` : ""} - upload a new one`
                    : lq.documentOverdue
                      ? "Document overdue - upload now"
                      : `Upload document (due ${lq.documentDeadline})`
                }
              >
                <Button
                  type="text"
                  danger={lq.documentOverdue || lq.documents?.[0]?.reviewStatus === "REJECTED"}
                  icon={<UploadOutlined />}
                  onClick={() => {
                    setAttachDocumentRequest(lq);
                    setIsAttachDocumentOpen(true);
                  }}
                />
              </Tooltip>
            )}
            <ViewDocumentsBtn documents={lq.documents} />
          </div>
        ),
      },
      {
        title: "Request ID",
        dataIndex: "id",
        key: "id",
        width: 100,
        sorter: (a: LeaveRequest, b: LeaveRequest) => a.id - b.id,
        sortDirections,
        render: (_: unknown, record: LeaveRequest) => (
          <RequestIdLink id={record.id} />
        ),
      },
      {
        title: "Request Type",
        dataIndex: "requestType",
        key: "requestType",
        width: 130,
        ellipsis: true,
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          formatEnumLabel(a.requestType).localeCompare(
            formatEnumLabel(b.requestType),
          ),
        sortDirections,
        render: (value: LeaveRequest["requestType"]) => formatEnumLabel(value),
      },
      {
        title: "Request Date",
        dataIndex: "requestCreationDate",
        key: "requestDate",
        dataType: "date",
        width: 140,
        ellipsis: true,
        render: (value: LeaveRequest["requestCreationDate"]) =>
          formatRequestedAt(value),
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.requestCreationDate || "").localeCompare(
            b.requestCreationDate || "",
          ),
        defaultSortOrder: "descend",
        sortDirections,
        // format the date to a more readable format, make it be day-month-year


      },
      {
        title: "Start Date",
        dataIndex: "startDate",
        key: "startDate",
        dataType: "date",
        width: 110,
        ellipsis: true,
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.startDate || "").localeCompare(b.startDate || ""),
        sortDirections,
        render: (value: LeaveRequest["startDate"]) => {
          if (!value) return "-";
          const date = new Date(value);
          return date.toLocaleDateString("en-GB");
        },
      },
      {
        title: "End Date",
        dataIndex: "endDate",
        key: "endDate",

        width: 110,
        ellipsis: true,
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.endDate || "").localeCompare(b.endDate || ""),
        sortDirections,
        render: (value: LeaveRequest["endDate"]) => {
          if (!value) return "-";
          const date = new Date(value);
          return date.toLocaleDateString("en-GB");
        },
      },
      {
        title: "Requested Days",
        key: "requestedDays",
        width: 150,
        ellipsis: true,
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.requestedDays || a.leaveDays?.length || 0) -
          (b.requestedDays || b.leaveDays?.length || 0),
        sortDirections,
        render: (_: unknown, record: LeaveRequest) =>
          record.requestedDays || record.leaveDays?.length || "-",
      },
      {
        title: "Substitute",
        dataIndex: "substituteName",
        key: "substituteName",
        width: 140,
        ellipsis: true,
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.substituteName || "").localeCompare(b.substituteName || ""),
        sortDirections,
      },
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
        width: 220,
        ellipsis: true,

        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.comment || "").localeCompare(b.comment || ""),
        sortDirections,
      },
      {
        title: "Status",
        dataIndex: "requestStatus",
        key: "requestStatus",
        width: 140,
        ellipsis: true,
        filters: statusFilterOptions,
        filteredValue: statusFilter.length ? statusFilter : null,
        onFilter: (value, record) => record.requestStatus === value,
        filterMultiple: false,
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          formatEnumLabel(a.requestStatus).localeCompare(
            formatEnumLabel(b.requestStatus),
          ),
        sortDirections,
        render: (value: LeaveRequest["requestStatus"]) => (
          <Tag className={`${getRequestStatusType(value)}`}>
            {formatEnumLabel(value)}
          </Tag>
        ),
      },
      {
        title: "Approver",
        dataIndex: "approverName",
        key: "approverName",
        width: 150,
        ellipsis: true,
        sorter: (a: LeaveRequest, b: LeaveRequest) =>
          (a.approverName || "").localeCompare(b.approverName || ""),
        sortDirections,
        render: (value: LeaveRequest["approverName"]) => value || "-",
      },
    ],
    [selfUser, statusFilter, statusFilterOptions, isMobile],
  );

  return (
    <div className="my-leave-requests">
      {documentsToUpload.length > 0 && (
        <div className="mb-6">
          <Typography.Title level={4} className="table-header !mb-2">
            Documents to Upload
          </Typography.Title>
          <Table
            className="view-table w-full"
            columns={documentUploadColumns}
            dataSource={documentsToUpload}
            rowKey={(record) => `${record.id}-${record.requestType}`}
            pagination={false}
            size="small"
          />
        </div>
      )}
      <div className="mb-4 flex items-center justify-between gap-3">
        <Typography.Title level={4} className="table-header !mb-0">
          Annual Leave Requests
        </Typography.Title>
        <div className="flex flex-wrap items-center gap-2">
          <AnnualLeavePlanModal
            isOpen={isAnnualPlanOpen}
            setIsOpen={setIsAnnualPlanOpen}
            year={currentYear}
            refreshData={refreshPageData}
          />
          <LeaveReqModal
            isOpen={isLeaveModalOpen}
            setIsOpen={setIsLeaveModalOpen}
            withButton
            refreshData={refreshPageData}
          />
        </div>
      </div>
      <div className="mb-2 flex justify-start">
        <RangePicker
          allowClear
          allowEmpty={[true, true]}
          format="DD/MM/YYYY"
          value={requestDateRange}
          onChange={(range) => setRequestDateRange(range)}
          placeholder={["Start date", "End date"]}
        />
      </div>
      <Row className="md:flex-row-reverse min-h-[400px]">
        {isLeaveBalanceVisible && (
          <Col xs={24} lg={8} className="mb-4 flex justify-center">
            {userData && (
              <DonutChart title="Leave balance" userData={userData} />
            )}
          </Col>
        )}
        <Col xs={24} lg={isLeaveBalanceVisible ? 16 : 24}>
          <div className="table-container flex h-full ">
            {!isMobile && (
              <div className="leave-balance-sticky-toggle">
                <Tooltip
                  title={
                    isLeaveBalanceVisible
                      ? "Hide leave balance"
                      : "Show leave balance"
                  }
                >
                  <Button
                    type="text"
                    shape="circle"
                    icon={
                      isLeaveBalanceVisible ? (
                        <RightOutlined />
                      ) : (
                        <LeftOutlined />
                      )
                    }
                    onClick={() => setIsLeaveBalanceVisible((prev) => !prev)}
                    aria-label={
                      isLeaveBalanceVisible
                        ? "Hide leave balance"
                        : "Show leave balance"
                    }
                  />
                </Tooltip>
              </div>
            )}
            <Table
              className="view-table h-full w-full [&_.ant-table]:flex [&_.ant-table]:h-full [&_.ant-table]:flex-col [&_.ant-table-container]:flex [&_.ant-table-container]:h-full [&_.ant-table-container]:flex-col [&_.ant-table-body]:flex-1 [&_.ant-table-pagination]:justify-center [&_.ant-table-cell]:h-11 [&_.ant-table-cell]:px-3 [&_.ant-table-cell]:py-2 [&_.ant-table-cell]:whitespace-nowrap [&_.ant-table-cell]:overflow-hidden [&_.ant-table-cell]:text-ellipsis"
              tableLayout="fixed"
              columns={tableColumns}
              locale={{
                emptyText: isLoadingData ? (
                  <div className="table-loading">
                    <Spin size="small" />
                  </div>
                ) : myLeaveRequests.length === 0 ? (
                  <Empty description="You have no open leave requests." />
                ) : filteredMyLeaveRequests.length === 0 ? (
                  <Empty description="No requests found for the selected date range." />
                ) : null,
              }}
              dataSource={filteredMyLeaveRequests}
              scroll={{ x: 1210, y: fixedTableScrollY }}
              rowKey={(record) => `${record.id}-${record.requestType}`}
              pagination={{
                current: currentPage,
                pageSize,
                showSizeChanger: true,
                pageSizeOptions: ["5", "10", "20", "50"],
              }}
              onChange={(pagination, tableFilters, _sorter, extra) => {
                if (extra.action === "paginate") {
                  setCurrentPage(pagination.current || 1);
                  setPageSize(pagination.pageSize || rowsPerPage);
                }

                if (extra.action === "filter") {
                  const nextStatusFilter = tableFilters.requestStatus as
                    | string[]
                    | null
                    | undefined;
                  setStatusFilter(nextStatusFilter || []);
                }
              }}
            />
          </div>
        </Col>
      </Row>
      <Row className="mb-10">
        <MyCalendar />
      </Row>
      <RequestStatusModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        request={selectedRequest ? selectedRequest : null}
        status={selectedRequestStatus}
        source={"self"}
        onCancelConfirm={fetchMyLeaveReq}
      />
      <AttachDocumentModal
        isOpen={isAttachDocumentOpen}
        setIsOpen={setIsAttachDocumentOpen}
        requestId={attachDocumentRequest?.id ?? null}
        documentType={
          (attachDocumentRequest &&
            DEFERRED_DOCUMENT_TYPE_BY_REQUEST_TYPE[
              attachDocumentRequest.requestType
            ]) ||
          ""
        }
        onUploaded={() => {
          fetchMyLeaveReq();
          refreshLeaveRequests();
        }}
      />
      <LeaveReqModal
        isOpen={isResubmitOpen}
        setIsOpen={setIsResubmitOpen}
        withButton={false}
        editRequest={resubmitRequest}
        refreshData={fetchMyLeaveReq}
      />
    </div>
  );
};

export default MyLeaveRequests;
